/**
 * Shared pan/zoom/fit helpers for the two Cytoscape views of a case (Evidence and
 * Consolidated). Wheel zoom (centred on the pointer), drag pan and touch pinch are
 * Cytoscape built-ins once userZoomingEnabled / userPanningEnabled are on; this module
 * pins those on and adds what the built-ins lack: stepwise zoom about the viewport
 * centre, fit, reset and the +/-/0 keyboard shortcuts. Pure helpers: no React.
 */
import type cytoscape from 'cytoscape';

export const ZOOM_STEP = 1.25;
export const FIT_PADDING = 55;

type Cy = cytoscape.Core;
export type ElesGetter = (cy: Cy) => cytoscape.Collection;

const alive = (cy: Cy | null | undefined): cy is Cy => !!cy && !(cy.destroyed?.() ?? false);

/** Make sure the user can zoom and pan with the pointer, wheel and touch. */
export function enableViewport(cy: Cy): void {
  cy.userZoomingEnabled(true);
  cy.userPanningEnabled(true);
  cy.zoomingEnabled(true);
  cy.panningEnabled(true);
}

/** Zoom by a factor about the centre of the visible viewport (clamped by min/maxZoom). */
export function zoomBy(cy: Cy | null | undefined, factor: number): void {
  if (!alive(cy)) return;
  const level = Math.min(cy.maxZoom(), Math.max(cy.minZoom(), cy.zoom() * factor));
  cy.zoom({ level, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
}

/** Fit the whole picture (the given elements, default all) in view. */
export function fitView(cy: Cy | null | undefined, getEles?: ElesGetter, padding = FIT_PADDING): void {
  if (!alive(cy)) return;
  const eles = getEles ? getEles(cy) : cy.elements();
  if (eles.length === 0) return;
  cy.fit(eles, padding);
}

/** Reset to 100% zoom with the picture centred. */
export function resetView(cy: Cy | null | undefined, getEles?: ElesGetter): void {
  if (!alive(cy)) return;
  const eles = getEles ? getEles(cy) : cy.elements();
  cy.zoom(Math.min(cy.maxZoom(), Math.max(cy.minZoom(), 1)));
  if (eles.length) cy.center(eles);
}

/** Map a key to a viewport action; returns true when the key was handled. */
export function handleViewportKey(
  e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>,
  cy: Cy | null | undefined,
  getEles?: ElesGetter,
): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  switch (e.key) {
    case '+': case '=': zoomBy(cy, ZOOM_STEP); return true;
    case '-': case '_': zoomBy(cy, 1 / ZOOM_STEP); return true;
    case '0': fitView(cy, getEles); return true;
    default: return false;
  }
}

/**
 * Keyboard zoom while the view has focus: makes the Cytoscape container focusable and
 * listens for + / - / 0 on it. Returns a detach function.
 */
export function attachViewportKeys(cy: Cy, getEles?: ElesGetter): () => void {
  const el = cy.container();
  if (!el) return () => {};
  if (el.tabIndex < 0) el.tabIndex = 0;
  el.setAttribute('aria-label', 'Graph view. Plus and minus zoom, 0 fits the whole picture.');
  const onKey = (e: KeyboardEvent) => {
    if (handleViewportKey(e, cy, getEles)) e.preventDefault();
  };
  el.addEventListener('keydown', onKey);
  return () => el.removeEventListener('keydown', onKey);
}
